import { add } from 'date-fns';
import {
  CHARCOAL,
  GRAY,
  IRON,
  NAVY,
  SHADOWS,
  SKY,
  WHITE_BACKGROUND
} from 'styles/Colors';
import {
  getReturningUserData,
  postPasswordlessEmail,
  postPasswordlessText
} from './ReturningUserApiCalls';
import { isUndefined } from 'lodash';
import { LargeMobile, Safari, SmallMobile } from 'styles/MediaQueries';

import ButtonGroup from '@opploans/button-group-component/ButtonGroup';
import CtaButton from '@opploans/cta-button-component/CtaButton';
import Fieldset from 'components/rebrand/Fieldset';
import loadingIcon from 'images/LoadingSpinner.gif';
import PersonaIcon from 'images/RebrandPersona.png';
import React, { useEffect, useState } from 'react';

const iconStyle = {
  height: '4.7rem',
  margin: '0'
};

const containerStyle = {
  position: 'relative',
  maxWidth: '35rem',
  margin: '6rem auto 10rem',
  textAlign: 'center',
  padding: '1.25rem',
  backgroundColor: WHITE_BACKGROUND,
  '& svg': {
    top: '-0.2rem',
    width: '5.6rem',
    height: '4rem',
    [LargeMobile]: {
      display: 'none'
    },
    [SmallMobile]: {
      display: 'none'
    }
  },
  [LargeMobile]: {
    border: '0px'
  },
  [SmallMobile]: {
    border: '0px'
  }
};

const fieldsetCustomStyle = {
  ' #large-text': {
    marginBottom: '48px'
  }
};

const iconContainerStyle = {
  left: 'calc(50% - 2.35rem - 10px)',
  top: '2.35rem',
  zIndex: 1,
  marginBottom: '2rem'
};

const titleStyle = {
  color: NAVY,
  fontWeight: 'bold',
  fontSize: '24px',
  lineHeight: '28px',
  marginBottom: '20px',
  [Safari]: {
    letterSpacing: '-1px'
  }
};

const subtitleStyle = {
  marginBottom: '8px',
  fontWeight: 'bold',
  fontSize: '18px',
  lineHeight: '24px'
};

const largeTextStyle = {
  margin: '48px 0',
  fontSize: '20px',
  lineHeight: '24px'
};

const smallTextStyle = {
  fontSize: '16px',
  lineHeight: '24px',
  '& a': {
    color: SKY
  }
};

const additionalStyle = {
  width: '100%',
  height: '3.2rem',
  '&:disabled, &:disabled:hover': {
    background: GRAY,
    color: IRON
  }
};

const loadingStyle = {
  display: 'flex',
  justifyContent: 'center',
  marginTop: '5rem'
};

const bodyStyle = {
  textAlign: 'left',
  padding: '0 15px'
};

const marginTop = {
  marginTop: '100px'
};

export default function ReturningUser() {
  const [codeSent, setCodeSent] = useState(false);
  const [method, setMethod] = useState();
  const [maskedPhone, setMaskedPhone] = useState();
  const [maskedEmail, setMaskedEmail] = useState();
  const [fullEmail, setFullEmail] = useState();
  const [fullPhone, setFullPhone] = useState();
  const [selectedMethod, setSelectedMethod] = useState();
  const [loading, setLoading] = useState(true);

  const maskEmail = (email) => {
    const index = email.search('@');
    setMaskedEmail(`${email.substr(0, 1)}*******${email.substr(index)}`);
  };

  useEffect(() => {
    // This is so that the footer will display after the form had loaded, see reg_redesign.slim
    if (document.getElementsByClassName('footer-container')[0]) {
      document.getElementsByClassName('footer-container')[0].style.display =
        'block';
    }
    if (document.getElementsByClassName('login-banner-container')[0]) {
      document.getElementsByClassName(
        'login-banner-container'
      )[0].style.marginTop = '4rem';
    }
    const body = document.getElementsByClassName(
      'application-returning-user'
    )[0];
    if (body.classList.contains('desktop-device')) {
      body.style.backgroundImage = `radial-gradient(circle, ${SHADOWS} 6%, transparent 5%)`;
      body.style.backgroundSize = '20px 20px';
    }
    body.style.overflowY = 'scroll';

    let returningUserData = {};
    async function fetchData() {
      try {
        returningUserData = await getReturningUserData();
        if (returningUserData.email) {
          setLoading(false);
          setFullEmail(returningUserData.email);
          setLoading(false);
          window.postHeapAnalytic('addUserProperties', {
            email: returningUserData.email
          });
          if (returningUserData.phone_match && returningUserData.email_match) {
            setMethod('phone or email');
            setMaskedPhone(`(***) *** - ${returningUserData.phone.substr(-4)}`);
            setFullPhone(returningUserData.phone);
            maskEmail(returningUserData.email);
          } else if (returningUserData.phone_match) {
            setMethod('phone');
            setMaskedPhone(`(***) *** - ${returningUserData.phone.substr(-4)}`);
            setFullPhone(returningUserData.phone);
            setSelectedMethod('mobile phone');
          } else {
            setMethod('email');
            maskEmail(returningUserData.email);
            setSelectedMethod('email');
          }
          if (
            parseInt(
              window.sessionStorage.getItem('passwordlessLinkSent'),
              10
            ) > Date.now()
          ) {
            setCodeSent(true);
          }
        }
      } catch (e) {
        /* eslint-disable no-console */
        console.error(e);
        /* eslint-enable no-console */
      }
    }
    fetchData();
  }, []);

  const options = [
    {
      value: 'mobile phone',
      displayValue: maskedPhone
    },
    {
      value: 'email',
      displayValue: maskedEmail
    }
  ];

  const phoneOrEmailText = () => {
    return (
      <>
        {method === 'phone' || method === 'email' ? (
          <>
            <p>Your secure link will be sent to</p>{' '}
            <p>{method === 'phone' ? maskedPhone : maskedEmail}</p>
          </>
        ) : (
          <>
            <p css={{ marginBottom: '20px' }}>Should we text or email?</p>{' '}
            <ButtonGroup
              direction='column'
              options={options}
              onClick={(value) => setSelectedMethod(value)}
            />
          </>
        )}
      </>
    );
  };

  const handleClick = () => {
    setCodeSent(true);
    const inFifteenMinutes = add(new Date(), { minutes: 15 }).getTime();
    window.sessionStorage.setItem('passwordlessLinkSent', inFifteenMinutes);
    if (selectedMethod === 'email') {
      postPasswordlessEmail(fullEmail);
    } else {
      postPasswordlessText(fullEmail, fullPhone).then((data) => {
        if (data.status !== 'delivered')
          window.toastr.error(
            null,
            'Message could not be delivered to your device. Please login with email and password instead.',
            { timeOut: 0, extendedTimeOut: 0 }
          );
      });
    }
  };

  return (
    <>
      {!loading && (
        <div>
          <Fieldset style={containerStyle} customStyle={fieldsetCustomStyle}>
            <div css={iconContainerStyle}>
              <img src={PersonaIcon} alt='persona' css={iconStyle} />
            </div>
            <h1 css={titleStyle}>
              {!codeSent ? (
                <>It looks like you’ve been here&nbsp;before.</>
              ) : (
                <>Secure link has been&nbsp;sent</>
              )}
            </h1>
            {!codeSent && <p css={subtitleStyle}>Welcome back!</p>}
            {codeSent && (
              <p css={[titleStyle, { color: CHARCOAL }]}>
                Check your {selectedMethod === 'email' ? 'email' : 'messages'}!
              </p>
            )}
            {!codeSent && (
              <p css={bodyStyle}>
                We’ll send a secure link to your {method}, allowing you to log
                in to your account without a password and continue your
                new&nbsp;application.
              </p>
            )}
            {!codeSent ? (
              <p css={largeTextStyle} id='large-text'>
                {phoneOrEmailText()}
              </p>
            ) : (
              <p css={{ ...largeTextStyle, ...bodyStyle }} id='large-text'>
                Please note, this link may take up to one minute to be received,
                may only be used once, and will expire in 30&nbsp;minutes.{' '}
              </p>
            )}
            {!codeSent && (
              <CtaButton
                disabled={isUndefined(selectedMethod)}
                onClick={handleClick}
                additionalStyle={additionalStyle}
              >
                Send Link
              </CtaButton>
            )}

            {!codeSent ? (
              <p css={smallTextStyle}>
                <a href='/auth/login'>Login with email and password</a>
              </p>
            ) : (
              <p css={{ ...smallTextStyle, ...marginTop }}>
                If you can’t access the secure link, you can&nbsp;
                <a href='/auth/login'>
                  log&nbsp;in&nbsp;with&nbsp;your&nbsp;password
                </a>
                &nbsp;instead.
              </p>
            )}
          </Fieldset>
        </div>
      )}
      {loading && (
        <div css={loadingStyle}>
          <img src={loadingIcon} alt='Loading...' />
        </div>
      )}
    </>
  );
}
